<template>
  <div class="prod-list-page">
    <v-container fluid grid-list-xl>
      <v-layout row wrap>
        <v-flex xs12>
          <div class="list-hero">
            <p class="list-crumb">Productos / Listado</p>
            <h1 class="list-title">Listado de Productos</h1>
            <p class="list-subtitle">
              Consulte y administre la información de los productos registrados
              en el sistema.
            </p>
          </div>
        </v-flex>

        <v-flex xs12>
          <v-card class="prod-card">
            <v-card-text class="prod-card__toolbar">
              <v-layout row wrap align-center>
                <v-flex md5 sm6 xs12>
                  <v-text-field
                    v-model="search"
                    class="list-search"
                    append-icon="search"
                    clearable
                    hide-details
                    placeholder="Buscar por nombre de producto..."
                    solo
                    @keyup.enter="onSearch"
                    @click:append="onSearch"
                  ></v-text-field>
                </v-flex>
                <v-flex md4 sm4 xs12>
                  <v-select
                    no-data-text="Datos no disponibles"
                    :items="laboratories"
                    label="Laboratorio"
                    v-model="laboratory"
                    clearable
                    hide-details
                    solo
                  ></v-select>
                </v-flex>
                <v-flex md3 sm2 xs12 class="toolbar-actions">
                  <v-btn
                    icon
                    class="btn-search"
                    title="Buscar"
                    @click="onSearch"
                  >
                    <v-icon small>search</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    class="btn-download"
                    title="Exportar todos"
                    :loading="exportingAll"
                    @click="onDownload"
                  >
                    <v-icon small>cloud_download</v-icon>
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-card-text>

            <v-card-text class="prod-card__toolbar prod-card__toolbar--actions">
              <v-layout row wrap align-center>
                <v-flex xs12 class="toolbar-actions toolbar-actions--end">
                  <button
                    class="action-btn action-btn--csv"
                    @click="handleUploadCSV"
                  >
                    <v-icon small class="action-btn__icon">upload_file</v-icon>
                    Subir CSV
                  </button>
                  <button
                    class="action-btn action-btn--delete-all"
                    @click="deleting.show = true"
                  >
                    <v-icon small class="action-btn__icon">delete_sweep</v-icon>
                    Eliminar Productos
                  </button>
                  <button
                    class="action-btn action-btn--delete-lab-products"
                    :disabled="!laboratory"
                    :title="
                      laboratory
                        ? 'Eliminar todos los productos del laboratorio seleccionado'
                        : 'Selecciona un laboratorio en el filtro para habilitar'
                    "
                    @click="confirmDeleteProductsByLab"
                  >
                    <v-icon small class="action-btn__icon"
                      >domain_disabled</v-icon
                    >
                    Eliminar productos del laboratorio
                  </button>
                </v-flex>
              </v-layout>
            </v-card-text>

            <v-card-text class="prod-card__content hidden-sm-and-down">
              <div class="table-wrap">
                <table class="prod-table">
                  <thead>
                    <tr>
                      <th>Nombre del Producto</th>
                      <th>Presentación Comercial</th>
                      <th>Registro Sanitario</th>
                      <th>Expiración</th>
                      <th>Laboratorio</th>
                      <th>Órgano Anuente</th>
                      <th class="th-actions">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in sortedProducts" :key="item.id">
                      <td>
                        {{
                          item.product && item.product.name
                            ? item.product.name
                            : '—'
                        }}
                      </td>
                      <td>
                        <span
                          class="presentation-cell"
                          :title="
                            item.fxFormula ||
                              (item.product &&
                                item.product.commercialPresentation) ||
                              '—'
                          "
                        >
                          {{
                            item.fxFormula ||
                              (item.product &&
                                item.product.commercialPresentation) ||
                              '—'
                          }}
                        </span>
                      </td>
                      <td>{{ item.sanitaryReg || '—' }}</td>
                      <td>{{ dateFormat(item.expirationDate) }}</td>
                      <td>
                        {{
                          item.laboratory && item.laboratory.name
                            ? item.laboratory.name
                            : '—'
                        }}
                      </td>
                      <td>
                        <span
                          v-if="
                            item.organoAnuente ||
                              (item.product && item.product.organoAnuente)
                          "
                        >
                          {{
                            (item.organoAnuente && item.organoAnuente.name) ||
                              (item.product &&
                                item.product.organoAnuente &&
                                item.product.organoAnuente.name) ||
                              '—'
                          }}
                        </span>
                        <span v-else class="empty-cell">—</span>
                      </td>
                      <td>
                        <div class="action-group">
                          <v-btn
                            class="btn-edit"
                            icon
                            small
                            title="Editar producto"
                            @click="openEditModal(item)"
                          >
                            <v-icon small>edit</v-icon>
                          </v-btn>
                          <v-btn
                            v-if="hasSanitaryFile(item)"
                            class="btn-export"
                            icon
                            small
                            :loading="exportLoadingId === item.id"
                            title="Descargar"
                            @click="exportProductFile(item)"
                          >
                            <v-icon small>file_download</v-icon>
                          </v-btn>
                          <v-btn
                            class="btn-delete"
                            icon
                            small
                            title="Eliminar producto"
                            @click="confirmDeleteProduct(item)"
                          >
                            <v-icon small>delete</v-icon>
                          </v-btn>
                          <v-btn
                            v-if="item.laboratory && item.laboratory.id"
                            class="btn-delete-lab"
                            icon
                            small
                            title="Eliminar laboratorio completo"
                            @click="confirmDeleteLab(item)"
                          >
                            <v-icon small>domain_disabled</v-icon>
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="!pagination.loading && products.length === 0">
                      <td colspan="7" class="empty-state">
                        No se encontraron productos con los filtros aplicados.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-card-text>

            <v-card-text class="prod-card__content-mobile hidden-md-and-up">
              <div
                v-if="!pagination.loading && products.length === 0"
                class="mobile-empty"
              >
                No se encontraron productos con los filtros aplicados.
              </div>
              <v-layout row wrap>
                <v-flex
                  xs12
                  v-for="item in sortedProducts"
                  :key="`card-${item.id}`"
                >
                  <v-card class="prod-mobile-card">
                    <v-card-text>
                      <div class="mobile-row">
                        <span class="mobile-label">Producto</span>
                        <span class="mobile-value">{{
                          item.product && item.product.name
                            ? item.product.name
                            : '—'
                        }}</span>
                      </div>
                      <div class="mobile-row">
                        <span class="mobile-label">Presentación</span>
                        <span
                          class="mobile-value mobile-value--truncate"
                          :title="
                            item.fxFormula ||
                              (item.product &&
                                item.product.commercialPresentation) ||
                              '—'
                          "
                        >
                          {{
                            item.fxFormula ||
                              (item.product &&
                                item.product.commercialPresentation) ||
                              '—'
                          }}
                        </span>
                      </div>
                      <div class="mobile-row">
                        <span class="mobile-label">Registro</span>
                        <span class="mobile-value">{{
                          item.sanitaryReg || '—'
                        }}</span>
                      </div>
                      <div class="mobile-row">
                        <span class="mobile-label">Expiración</span>
                        <span class="mobile-value">{{
                          dateFormat(item.expirationDate)
                        }}</span>
                      </div>
                      <div class="mobile-row">
                        <span class="mobile-label">Laboratorio</span>
                        <span class="mobile-value">{{
                          item.laboratory && item.laboratory.name
                            ? item.laboratory.name
                            : '—'
                        }}</span>
                      </div>
                      <div
                        class="mobile-row"
                        v-if="
                          item.organoAnuente ||
                            (item.product && item.product.organoAnuente)
                        "
                      >
                        <span class="mobile-label">Órgano Anuente</span>
                        <span class="mobile-value">{{
                          (item.organoAnuente && item.organoAnuente.name) ||
                            (item.product &&
                              item.product.organoAnuente &&
                              item.product.organoAnuente.name) ||
                            '—'
                        }}</span>
                      </div>
                      <div class="mobile-actions">
                        <v-btn
                          class="btn-edit"
                          small
                          icon
                          @click="openEditModal(item)"
                          title="Editar"
                        >
                          <v-icon small>edit</v-icon>
                        </v-btn>
                        <v-btn
                          class="btn-delete"
                          small
                          icon
                          @click="confirmDeleteProduct(item)"
                          title="Eliminar producto"
                        >
                          <v-icon small>delete</v-icon>
                        </v-btn>
                        <v-btn
                          v-if="item.laboratory && item.laboratory.id"
                          class="btn-delete-lab"
                          small
                          icon
                          @click="confirmDeleteLab(item)"
                          title="Eliminar laboratorio"
                        >
                          <v-icon small>domain_disabled</v-icon>
                        </v-btn>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>

        <v-flex xs12 v-if="pagination.pages">
          <div class="list-footer">
            <div class="list-footer__count">
              Mostrando {{ products.length }} de
              {{ pagination.totalItems }} productos
            </div>
            <v-pagination
              v-model="pagination.page"
              :length="pagination.pages"
              :total-visible="7"
              @input="asyncPagination"
            ></v-pagination>
          </div>
        </v-flex>
      </v-layout>
    </v-container>

    <v-dialog max-width="500" v-model="csv.show">
      <upload-csv
        :key="csv.key"
        v-on:close-csv="csv.show = false"
        v-on:loading-show="loading = true"
        v-on:upload-ready="uploadReady"
      ></upload-csv>
    </v-dialog>

    <v-dialog max-width="350" persistent v-model="loading">
      <loading></loading>
    </v-dialog>

    <v-dialog max-width="500" persistent v-model="deleting.show">
      <deleting
        :key="deleting.key"
        v-on:loading-show="loading = true"
        v-on:close="deleting.show = false"
        v-on:delete-ready="deleteReady"
      ></deleting>
    </v-dialog>

    <v-dialog max-width="400" persistent v-model="processing">
      <v-card class="processing-card">
        <div class="processing-icon-wrap">
          <div class="processing-icon-circle">
            <v-icon class="processing-icon">sync</v-icon>
          </div>
        </div>
        <div class="processing-body">
          <h2 class="processing-title">Procesando archivo</h2>
          <p class="processing-subtitle">
            El archivo fue cargado. Estamos procesando las columnas, por favor
            espere...
          </p>
        </div>
        <div class="processing-bar-wrap">
          <v-progress-linear
            color="#254f87"
            indeterminate
            height="4"
            class="processing-bar"
          ></v-progress-linear>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog max-width="500" v-if="dialog.message" v-model="dialog.show">
      <message
        :key="dialog.key"
        :options="dialog"
        v-on:close="dialog.show = false"
      ></message>
    </v-dialog>

    <!-- Modal de edición del producto -->
    <v-dialog
      v-model="editDialog.show"
      max-width="980"
      persistent
      content-class="prod-edit-dialog"
    >
      <v-card class="prod-edit-dialog__card">
        <product-form
          v-if="editDialog.show && editDialog.productId != null"
          :key="`edit-${editDialog.productId}`"
          :productId="editDialog.productId"
          embedded
          @saved="onProductSaved"
          @cancel="closeEditModal"
        ></product-form>
      </v-card>
    </v-dialog>

    <dialog-confirm
      v-model="deleteFileDialog.show"
      :loading="deleteFileDialog.loading"
      title="Eliminar archivo"
      message="El archivo de registro sanitario será eliminado. El producto se mantendrá, pero quedará sin archivo asociado."
      @confirm="deleteFile"
    ></dialog-confirm>

    <dialog-confirm
      v-model="deleteProductDialog.show"
      :loading="deleteProductDialog.loading"
      title="Eliminar producto"
      message="Esta acción no se puede deshacer. El producto y toda la información asociada serán eliminados permanentemente del sistema."
      :item-name="deleteProductName"
      @confirm="deleteProduct"
    ></dialog-confirm>

    <dialog-confirm
      v-model="labDelete.show"
      :loading="labDelete.loading"
      title="Eliminar laboratorio completo"
      message="Esta acción eliminará el laboratorio del sistema de forma permanente junto con toda su configuración."
      :item-name="labDelete.name"
      @confirm="deleteLab"
    ></dialog-confirm>

    <dialog-confirm
      v-model="labProductsDelete.show"
      :loading="labProductsDelete.loading"
      title="Eliminar productos del laboratorio"
      message="Se deshabilitarán todos los productos y sus lotes de este laboratorio (no se afectan los que ya están en facturas). Esta acción es reversible."
      :item-name="labProductsDelete.name"
      @confirm="deleteProductsByLab"
    ></dialog-confirm>

    <v-dialog
      v-model="filePreviewDialog.show"
      max-width="920"
      @input="
        value => {
          if (!value) closeFilePreview()
        }
      "
    >
      <v-card class="modal-card">
        <div class="modal-header">
          <h3 class="modal-header__title">
            {{ filePreviewDialog.title || 'Vista previa de archivo' }}
          </h3>
        </div>
        <v-card-text class="modal-body modal-body--preview">
          <img
            v-if="isImagePreview(filePreviewDialog)"
            :src="filePreviewDialog.url"
            alt="Vista previa"
            class="file-preview-image"
          />
          <iframe
            v-else-if="isPdfPreview(filePreviewDialog)"
            :src="filePreviewDialog.url"
            class="file-preview-pdf"
            title="Vista previa PDF"
          ></iframe>
          <div v-else class="file-preview-fallback">
            <v-icon class="file-preview-fallback__icon"
              >insert_drive_file</v-icon
            >
            <p>No se pudo previsualizar este archivo.</p>
            <a :href="filePreviewDialog.url" target="_blank" rel="noopener"
              >Abrir archivo en una nueva pestaña</a
            >
          </div>
        </v-card-text>
        <div class="modal-footer">
          <v-btn class="btn-cancel" flat small @click="closeFilePreview"
            >Cerrar</v-btn
          >
          <v-btn
            class="btn-save"
            small
            :href="filePreviewDialog.url"
            target="_blank"
            rel="noopener"
            >Abrir en nueva pestaña</v-btn
          >
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { setTimeout } from 'timers'
import * as XLSX from 'xlsx'
import Api from '@/api/index'
import { dateFormat } from '@/helpers/TransitHelper'
import loading from './../dialog/DialogLoading'
import importProduct from './../dialog/ImportProduct'
import deleting from './../dialog/DialogDeleteAll'
import message from './../dialog/Message'
import ProductForm from './Form.vue'
import DialogConfirm from './../dialog/DialogConfirm'

export default {
  components: {
    loading,
    message,
    'upload-csv': importProduct,
    deleting,
    ProductForm,
    DialogConfirm,
  },
  data() {
    return {
      search: '',
      laboratory: '',
      loading: true,
      laboratories: [],
      pagination: {
        page: 1,
        rpp: this.$config.resultsPerPage,
        totalItems: 0,
        loading: true,
        pages: 0,
        search: {},
      },
      processing: false,
      csv: {
        show: null,
        data: null,
        key: 0,
      },
      dialog: {
        show: null,
        key: 0,
      },
      previewLoadingId: null,
      exportLoadingId: null,
      exportingAll: false,
      filePreviewObjectUrl: '',
      filePreviewDialog: {
        show: false,
        title: '',
        url: '',
        contentType: '',
      },
      deleting: {
        show: null,
        key: 0,
      },
      editDialog: {
        show: false,
        productId: null,
      },
      deleteFileDialog: {
        show: false,
        loading: false,
        item: null,
      },
      deleteProductDialog: {
        show: false,
        loading: false,
        item: null,
      },
      labDelete: {
        show: false,
        loading: false,
        id: null,
        name: '',
      },
      labProductsDelete: {
        show: false,
        loading: false,
        id: null,
        name: '',
      },
    }
  },
  created() {
    this.$store.dispatch('laboratories/getAll', {}).then(response => {
      const data = response.items
      for (let index = 0; index < data.length; index += 1) {
        this.laboratories.push({
          text: data[index].name,
          value: data[index].id,
        })
      }
    })

    this.asyncPagination()
  },
  computed: {
    ...mapState({
      products: state => state.products.all,
    }),
    sortedProducts() {
      return [...this.products].sort((a, b) => {
        const nameA = (a.product && a.product.name) || ''
        const nameB = (b.product && b.product.name) || ''
        return nameA.localeCompare(nameB, 'es', { sensitivity: 'base' })
      })
    },
    deleteProductName() {
      const item = this.deleteProductDialog.item
      const name = item && item.product && item.product.name
      return name || 'este producto'
    },
    selectedLabName() {
      const match = this.laboratories.find(lab => lab.value === this.laboratory)
      return match ? match.text : ''
    },
  },
  beforeDestroy() {
    this.clearPreviewObjectUrl()
  },
  methods: {
    hasSanitaryFile(item) {
      const filePath = item && item.product && item.product.sanitaryRegFile
      return typeof filePath === 'string'
        ? filePath.trim().length > 0
        : !!filePath
    },
    getSanitaryFileName(item) {
      const filePath = item && item.product && item.product.sanitaryRegFile
      if (!filePath || typeof filePath !== 'string') return 'documento.pdf'
      const parts = filePath.split('/')
      return parts[parts.length - 1] || 'documento.pdf'
    },
    getSanitaryFileDate(item) {
      const rawDate =
        (item && item.product && item.product.updatedAt) ||
        (item && item.product && item.product.createdAt) ||
        (item && item.updatedAt) ||
        (item && item.createdAt)
      return rawDate ? dateFormat(rawDate, 'DD/MM/YYYY') : 'Sin fecha'
    },
    getContentType(headers) {
      if (!headers) return ''
      return headers['content-type'] || headers['Content-Type'] || ''
    },
    getFileNameFromHeaders(headers) {
      if (!headers) return ''
      const contentDisposition =
        headers['content-disposition'] || headers['Content-Disposition'] || ''
      const match = /filename\*=UTF-8''([^;]+)|filename=\"?([^\";]+)\"?/i.exec(
        contentDisposition
      )
      const encodedName = match && (match[1] || match[2])
      return encodedName ? decodeURIComponent(encodedName) : ''
    },
    isImagePreview(preview) {
      const mime = (preview && preview.contentType
        ? preview.contentType
        : ''
      ).toLowerCase()
      if (mime.indexOf('image/') === 0) return true
      const title = (preview && preview.title
        ? preview.title
        : ''
      ).toLowerCase()
      return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(title)
    },
    isPdfPreview(preview) {
      const mime = (preview && preview.contentType
        ? preview.contentType
        : ''
      ).toLowerCase()
      if (mime.indexOf('application/pdf') === 0) return true
      const title = (preview && preview.title
        ? preview.title
        : ''
      ).toLowerCase()
      return /\.pdf$/.test(title)
    },
    clearPreviewObjectUrl() {
      if (this.filePreviewObjectUrl) {
        URL.revokeObjectURL(this.filePreviewObjectUrl)
        this.filePreviewObjectUrl = ''
      }
    },
    closeFilePreview() {
      this.filePreviewDialog.show = false
      this.clearPreviewObjectUrl()
    },
    async openProductFilePreview(item) {
      if (!this.hasSanitaryFile(item)) return
      this.previewLoadingId = item.id
      this.clearPreviewObjectUrl()
      try {
        const response = await Api.Product.getDocumentByProduct(item.id)
        if (!response || response.status !== 200 || !response.data) {
          throw new Error('No se pudo obtener el documento del producto.')
        }

        const contentType = this.getContentType(response.headers)
        const fileName =
          this.getFileNameFromHeaders(response.headers) || `producto-${item.id}`
        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], {
                type: contentType || 'application/octet-stream',
              })

        this.filePreviewObjectUrl = URL.createObjectURL(blob)
        this.filePreviewDialog = {
          show: true,
          title: fileName,
          url: this.filePreviewObjectUrl,
          contentType: blob.type || contentType || '',
        }
      } catch (error) {
        this.dialog = {
          message: 'No se pudo cargar el archivo del producto.',
          color: 'error',
          show: true,
          key: this.dialog.key + 1,
        }
      } finally {
        this.previewLoadingId = null
      }
    },
    async exportProductFile(item) {
      if (!this.hasSanitaryFile(item)) return
      this.exportLoadingId = item.id
      try {
        const response = await Api.Product.getDocumentByProduct(item.id)
        if (!response || response.status !== 200 || !response.data) {
          throw new Error('No se pudo exportar el archivo del producto.')
        }

        const contentType = this.getContentType(response.headers)
        const fileName =
          this.getFileNameFromHeaders(response.headers) || `producto-${item.id}`
        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], {
                type: contentType || 'application/octet-stream',
              })

        const downloadUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(downloadUrl)
      } catch (error) {
        this.dialog = {
          message: 'No se pudo exportar el archivo del producto.',
          color: 'error',
          show: true,
          key: this.dialog.key + 1,
        }
      } finally {
        this.exportLoadingId = null
      }
    },
    helperPaginate(response) {
      this.pagination.totalItems = response.total
      this.pagination.pages =
        Math.ceil(this.pagination.totalItems / this.pagination.rpp) || 0
      this.loading = false
      this.pagination.loading = false
    },
    asyncPagination() {
      this.pagination.loading = true

      let filter = {
        rpp: this.pagination.rpp,
        page: this.pagination.page,
        ...this.pagination.search,
      }

      return new Promise(resolve => {
        this.$store.dispatch('products/getAll', filter).then(response => {
          this.helperPaginate(response)
          return resolve()
        })
      })
    },
    onSearch() {
      const params = { term: this.search, labId: this.laboratory }

      Object.keys(params).forEach(function(key) {
        if (params[key] == null || params[key] == '') delete params[key]
      })

      this.pagination.search = params
      this.pagination.page = 1
      this.asyncPagination()
    },
    dateFormat,
    handleUploadCSV() {
      this.csv = {
        show: true,
        key: this.csv.key + 1,
      }
    },
    uploadReady(response) {
      this.loading = false
      this.processing = true
      this.$socket.emit('importProd', {
        fileImport: response.file,
        labId: response.labId,
      })
    },
    async onDownload() {
      if (this.exportingAll) return
      this.exportingAll = true
      try {
        const { rpp, page, ...searchOnly } = this.pagination.search || {}
        const response = await Api.Product.exportAll(searchOnly)

        if (!response || response.status !== 200 || !response.data) {
          throw new Error('Sin respuesta')
        }

        // Normalizar: puede venir como { items: [] } o directamente []
        const raw = response.data
        const items = Array.isArray(raw)
          ? raw
          : Array.isArray(raw.items)
          ? raw.items
          : Array.isArray(raw.data)
          ? raw.data
          : Object.values(raw).find(v => Array.isArray(v)) || []

        // Armar filas del Excel
        const rows = items.map(item => ({
          Lote: item['Lote'] || '',
          Producto: item['Producto'] || '',
          'Organismo Anuente': item['organoAnuente'] || '',
          'Reg. Sanitario': item['Reg. Sanitario'] || '',
          Fórmula: item['Formula'] || '',
          'Fecha Expiración': item['Fecha Expiracion'] || '',
          Laboratorio: item['Laboratorio'] || '',
          Certificado: item['Certificado'] || '',
          'Total Certificados':
            item['Total Certificados'] != null
              ? item['Total Certificados']
              : '',
          Estatus: item['Estatus'] || '',
          Habilitado: item['Habilitado'] || '',
        }))

        const ws = XLSX.utils.json_to_sheet(rows)

        // Ancho de columnas
        ws['!cols'] = [
          { wch: 12 }, // Lote
          { wch: 38 }, // Producto
          { wch: 32 }, // Organismo Anuente
          { wch: 16 }, // Reg. Sanitario
          { wch: 45 }, // Fórmula
          { wch: 16 }, // Fecha Expiración
          { wch: 25 }, // Laboratorio
          { wch: 12 }, // Certificado
          { wch: 16 }, // Total Certificados
          { wch: 12 }, // Estatus
          { wch: 10 }, // Habilitado
        ]

        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Productos')

        const today = new Date().toISOString().slice(0, 10)
        XLSX.writeFile(wb, `productos-${today}.xlsx`)
      } catch (e) {
        this.dialog = {
          message: 'No se pudo exportar el listado de productos.',
          color: 'error',
          show: true,
          key: this.dialog.key + 1,
        }
      } finally {
        this.exportingAll = false
      }
    },
    deleteReady(total) {
      this.loading = false
      this.asyncPagination().then(() => {
        setTimeout(() => {
          this.loading = false
          this.dialog = {
            message: `(${total}) productos fueron eliminados del sistema`,
            color: 'success',
            show: true,
            key: this.dialog.key + 1,
          }
        }, 300)
      })
    },
    productIdFromItem(item) {
      if (!item) return null
      if (item.id != null) return item.id
      if (item.productId != null) return item.productId
      if (item.product && item.product.id != null) return item.product.id
      return null
    },
    openEditModal(item) {
      const id = this.productIdFromItem(item)
      if (id == null) return
      this.editDialog = { show: true, productId: id }
    },
    closeEditModal() {
      this.editDialog = { show: false, productId: null }
    },
    onProductSaved() {
      this.closeEditModal()
      this.dialog = {
        message: 'Producto actualizado correctamente.',
        color: 'success',
        show: true,
        key: this.dialog.key + 1,
      }
      this.asyncPagination()
    },
    confirmDeleteFile(item) {
      this.deleteFileDialog = {
        show: true,
        loading: false,
        item,
      }
    },
    async deleteFile() {
      const item = this.deleteFileDialog.item
      if (!item) return
      this.deleteFileDialog.loading = true
      try {
        const id = this.productIdFromItem(item)
        const payload = {
          id,
          removeSanitaryFile: true,
          sanitaryRegFile: null,
        }
        const response = await this.$store.dispatch('products/save', payload)
        if (response && response.status >= 400) throw new Error('http')

        this.deleteFileDialog = { show: false, loading: false, item: null }
        this.dialog = {
          message: 'Archivo eliminado correctamente.',
          color: 'success',
          show: true,
          key: this.dialog.key + 1,
        }
        this.asyncPagination()
      } catch (e) {
        this.deleteFileDialog.loading = false
        this.dialog = {
          message: 'No se pudo eliminar el archivo. Intenta de nuevo.',
          color: 'error',
          show: true,
          key: this.dialog.key + 1,
        }
      }
    },
    confirmDeleteProduct(item) {
      this.deleteProductDialog = { show: true, loading: false, item }
    },
    deleteProduct() {
      const item = this.deleteProductDialog.item
      if (!item) return
      const id = item.id
      if (id == null) return
      this.deleteProductDialog.loading = true
      this.$store
        .dispatch('products/deleteProductById', id)
        .then(response => {
          this.deleteProductDialog = { show: false, loading: false, item: null }
          if (response && response.status === 401) {
            this.dialog = {
              message:
                'El producto no puede ser eliminado, se encuentra asociado dentro del sistema.',
              color: 'error',
              show: true,
              key: this.dialog.key + 1,
            }
          } else {
            this.pagination.page = 1
            this.asyncPagination().then(() => {
              this.dialog = {
                message: 'El producto fue eliminado del sistema.',
                color: 'success',
                show: true,
                key: this.dialog.key + 1,
              }
            })
          }
        })
        .catch(() => {
          this.deleteProductDialog.loading = false
          this.dialog = {
            message: 'No se pudo eliminar el producto. Intenta de nuevo.',
            color: 'error',
            show: true,
            key: this.dialog.key + 1,
          }
        })
    },
    confirmDeleteLab(item) {
      const lab = item && item.laboratory
      if (!lab || !lab.id) return
      this.labDelete = {
        show: true,
        loading: false,
        id: lab.id,
        name: lab.name || '',
      }
    },
    deleteLab() {
      this.labDelete.loading = true
      this.$store
        .dispatch('laboratories/delete', { id: this.labDelete.id })
        .then(response => {
          this.labDelete.loading = false
          this.labDelete.show = false
          if (
            response &&
            (response.status === 200 || response.status === 204)
          ) {
            this.pagination.page = 1
            this.asyncPagination().then(() => {
              this.dialog = {
                message: 'Laboratorio eliminado correctamente.',
                color: 'success',
                show: true,
                key: this.dialog.key + 1,
              }
            })
          } else {
            this.dialog = {
              message:
                (response && response.data && response.data.error) ||
                'No se pudo eliminar el laboratorio.',
              color: 'error',
              show: true,
              key: this.dialog.key + 1,
            }
          }
        })
        .catch(() => {
          this.labDelete.loading = false
          this.dialog = {
            message: 'No se pudo conectar al servidor.',
            color: 'error',
            show: true,
            key: this.dialog.key + 1,
          }
        })
    },
    confirmDeleteProductsByLab() {
      if (!this.laboratory) return
      this.labProductsDelete = {
        show: true,
        loading: false,
        id: this.laboratory,
        name: this.selectedLabName || 'este laboratorio',
      }
    },
    deleteProductsByLab() {
      const labId = this.labProductsDelete.id
      if (labId == null) return
      this.labProductsDelete.loading = true
      this.$store
        .dispatch('products/deleteProductsByLab', { labId })
        .then(response => {
          this.labProductsDelete = {
            show: false,
            loading: false,
            id: null,
            name: '',
          }
          if (
            response &&
            (response.status === 200 || response.status === 204)
          ) {
            const affected =
              (response.data && response.data.affected != null
                ? response.data.affected
                : 0) || 0
            this.pagination.page = 1
            this.asyncPagination().then(() => {
              this.dialog = {
                message: `Se deshabilitaron ${affected} producto(s) del laboratorio.`,
                color: 'success',
                show: true,
                key: this.dialog.key + 1,
              }
            })
          } else {
            this.dialog = {
              message:
                (response && response.data && response.data.error) ||
                'No se pudieron eliminar los productos del laboratorio.',
              color: 'error',
              show: true,
              key: this.dialog.key + 1,
            }
          }
        })
        .catch(() => {
          this.labProductsDelete.loading = false
          this.dialog = {
            message: 'No se pudo conectar al servidor.',
            color: 'error',
            show: true,
            key: this.dialog.key + 1,
          }
        })
    },
  },
  sockets: {
    importReady(response) {
      console.log('[importReady]', response)
      const errors = response.rowsError

      this.asyncPagination().then(() => {
        if (errors.length > 0) {
          setTimeout(() => {
            const chips = errors
              .map(val => `<span class="err-chip">Fila ${val}</span>`)
              .join('')

            this.processing = false
            this.dialog = {
              message: `
                <div style="text-align:left">
                  <p style="margin:0 0 10px;color:#64748b;font-size:13px">
                    Se encontraron <strong style="color:#e5252a">${
                      errors.length
                    } filas</strong> que no pudieron procesarse:
                  </p>
                  <div style="display:flex;flex-wrap:wrap;gap:6px;max-height:140px;overflow-y:auto;padding:2px 0">
                    ${chips}
                  </div>
                  <p style="margin:10px 0 0;color:#94a3b8;font-size:12px">Verifique el formato de esas filas en el archivo CSV e intente nuevamente.</p>
                </div>`,
              color: 'error',
              show: true,
              key: this.dialog.key + 1,
            }
          }, 3000)
        } else {
          setTimeout(() => {
            this.processing = false
            this.loading = false
            this.dialog = {
              message: 'Los productos fueron importados correctamente',
              color: 'success',
              show: true,
              key: this.dialog.key + 1,
            }
          }, 3000)
        }
      })
    },
  },
}
</script>

<style scoped>
.prod-list-page {
  background: #f8fafc;
  min-height: 100vh;
  padding-bottom: 16px;
}

.list-hero {
  padding: 4px 0 8px;
}

.list-crumb {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.list-title {
  margin: 4px 0;
  font-size: 32px;
  line-height: 1.1;
  color: #0f172a;
  font-weight: 700;
}

.list-subtitle {
  margin: 0;
  font-size: 14px;
  color: #475569;
}

.prod-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  background: #ffffff;
}

.prod-card__toolbar {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.prod-card__toolbar--actions {
  background: #fcfdff;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  border: none;
  cursor: pointer;
  transition: all 0.18s ease;
  text-transform: none;
}

.action-btn__icon {
  font-size: 16px !important;
}

.action-btn--csv {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.action-btn--csv:hover {
  background: #dcfce7;
  border-color: #86efac;
}

.action-btn--delete-all {
  background: #fef2f2;
  color: #e5252a;
  border: 1px solid #fee2e2;
}

.action-btn--delete-all:hover {
  background: #fee2e2;
  border-color: #fca5a5;
}

.action-btn--delete-lab-products {
  background: #fffbeb;
  color: #d97706;
  border: 1px solid #fde68a;
}

.action-btn--delete-lab-products:hover:not(:disabled) {
  background: #fef3c7;
  border-color: #fcd34d;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.toolbar-actions--end {
  justify-content: flex-end;
}

.btn-search,
.btn-download {
  border: 1px solid rgba(37, 79, 135, 0.3) !important;
  color: #254f87 !important;
  background: rgba(37, 79, 135, 0.1) !important;
  box-shadow: none !important;
}

.prod-card__content {
  padding: 0;
}

.table-wrap {
  width: 100%;
  overflow-x: auto;
}

.prod-table {
  width: 100%;
  border-collapse: collapse;
}

.prod-table thead tr {
  background: #f8fafc;
}

.prod-table th,
.prod-table td {
  text-align: left;
  color: #334155;
  padding: 10px 12px;
  border-bottom: 1px solid #eef2f7;
  font-size: 14px;
}

.prod-table th {
  font-size: 12px;
  letter-spacing: 0.2px;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
}

.prod-table tbody tr:hover {
  background: #f8fafc;
}

.empty-state {
  text-align: center !important;
  color: #64748b;
  padding: 18px !important;
}

.presentation-cell {
  display: inline-block;
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
}

.empty-cell {
  color: #94a3b8;
}

.th-actions {
  width: 110px;
}

.action-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.reg-file-btn {
  border: 1px solid #dbe5f2 !important;
  background: #f8fbff !important;
  color: #172133 !important;
  border-radius: 8px !important;
  text-transform: none !important;
  box-shadow: none !important;
  min-width: 0 !important;
  height: 34px !important;
  padding: 0 10px !important;
}

.reg-file-btn__file-icon {
  color: #e5252a !important;
  margin-right: 6px;
}

.reg-file-btn__code {
  font-size: 12px;
  font-weight: 700;
}

.reg-file-btn__eye {
  color: #254f87 !important;
  margin-left: 8px;
}

.reg-file-btn--mobile {
  margin-left: auto;
}

/* === Card oscuro de Registro Sanitario === */
.reg-card {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: #0f172a;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 10px;
  padding: 8px 10px;
  min-height: 56px;
  min-width: 240px;
  max-width: 340px;
  color: #ffffff;
  transition: border-color 0.15s ease;
}

.reg-card:hover {
  border-color: rgba(148, 163, 184, 0.35);
}

.reg-card__icon {
  color: #ef4444 !important;
  font-size: 30px !important;
  flex-shrink: 0;
}

.reg-card__text {
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

.reg-card__name {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.reg-card__date {
  margin: 2px 0 0;
  font-size: 11px;
  color: rgba(148, 163, 184, 0.85);
}

.reg-card__actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.reg-action {
  width: 32px !important;
  height: 32px !important;
  margin: 0 !important;
  border-radius: 6px !important;
  border: 1px solid rgba(148, 163, 184, 0.18) !important;
  box-shadow: none !important;
  transition: all 0.15s ease;
}

.reg-action--edit {
  background: rgba(148, 163, 184, 0.1) !important;
  color: rgba(255, 255, 255, 0.85) !important;
}

.reg-action--edit:hover {
  background: rgba(148, 163, 184, 0.22) !important;
  border-color: rgba(148, 163, 184, 0.4) !important;
}

.reg-action--delete {
  background: rgba(229, 37, 42, 0.18) !important;
  color: #ff5d62 !important;
  border-color: rgba(229, 37, 42, 0.3) !important;
}

.reg-action--delete:hover {
  background: rgba(229, 37, 42, 0.3) !important;
  border-color: rgba(229, 37, 42, 0.55) !important;
}

.reg-empty {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #94a3b8;
  font-size: 13px;
}

.reg-action--add {
  width: 28px !important;
  height: 28px !important;
}

.btn-danger {
  background: #e5252a !important;
  color: #ffffff !important;
}

.btn-danger:hover {
  background: #c91e23 !important;
}

.text-muted {
  color: #94a3b8;
  font-size: 12.5px;
}

/* === Estilos del modal del producto === */
.modal-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.modal-header {
  padding: 18px 20px 14px;
  border-bottom: 1px solid #eef2f7;
}

.modal-header__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #172133;
}

.modal-body {
  padding: 14px 20px !important;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px 18px;
  border-top: 1px solid #eef2f7;
}

.product-file-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.sanitary-reg-only {
  color: #334155;
  font-weight: 600;
}

.sanitary-file-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: 1px solid #dbe5f2;
  border-radius: 8px;
  padding: 6px 8px;
  background: #f8fbff;
  min-width: 240px;
}

.sanitary-file-card__info {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.sanitary-file-card__icon {
  color: #e5252a !important;
  font-size: 22px !important;
}

.sanitary-file-card__text {
  min-width: 0;
}

.sanitary-file-card__name {
  margin: 0;
  font-size: 12px;
  color: #172133;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sanitary-file-card__meta {
  margin: 2px 0 0;
  font-size: 11px;
  color: #64748b;
}

.sanitary-file-card__actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.sanitary-file-card--mobile {
  margin: 6px 0 10px;
  min-width: 0;
}

.btn-preview {
  border: 1px solid rgba(37, 79, 135, 0.3) !important;
  color: #254f87 !important;
  background: rgba(37, 79, 135, 0.1) !important;
}

.btn-export {
  border: 1px solid rgba(229, 37, 42, 0.25) !important;
  color: #e5252a !important;
  background: rgba(229, 37, 42, 0.08) !important;
}

.list-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.list-footer__count {
  font-size: 13px;
  color: #64748b;
}

.prod-card__content-mobile {
  padding: 12px;
}

.prod-mobile-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  margin-bottom: 10px;
}

.mobile-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.mobile-label {
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  flex-shrink: 0;
}

.mobile-value {
  color: #334155;
  font-size: 13px;
  text-align: right;
}

.mobile-value--truncate {
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}

.mobile-empty {
  text-align: center;
  padding: 16px;
  color: #64748b;
  font-size: 13px;
}

.modal-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.modal-header {
  padding: 18px 20px 14px;
  border-bottom: 1px solid #eef2f7;
}

.modal-header__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #172133;
}

.modal-body {
  padding: 14px 20px !important;
}

.modal-body--preview {
  min-height: 320px;
}

.file-preview-image {
  display: block;
  max-width: 100%;
  max-height: 70vh;
  margin: 0 auto;
  border-radius: 8px;
}

.file-preview-pdf {
  width: 100%;
  height: 70vh;
  border: 0;
  border-radius: 8px;
  background: #f8fafc;
}

.file-preview-fallback {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #64748b;
}

.file-preview-fallback__icon {
  font-size: 42px !important;
  color: #94a3b8 !important;
}

.btn-cancel {
  border: 1px solid #e2e8f0 !important;
  background: transparent !important;
  color: #475569 !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  box-shadow: none !important;
}

.btn-save {
  background: #e5252a !important;
  color: #ffffff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  box-shadow: none !important;
}

.btn-edit {
  border: 1px solid rgba(37, 79, 135, 0.3) !important;
  color: #254f87 !important;
  background: rgba(37, 79, 135, 0.1) !important;
  box-shadow: none !important;
}

.btn-delete {
  border: 1px solid rgba(229, 37, 42, 0.25) !important;
  color: #e5252a !important;
  background: rgba(229, 37, 42, 0.07) !important;
  box-shadow: none !important;
}

.btn-delete:hover {
  background: rgba(229, 37, 42, 0.16) !important;
}

.btn-delete-lab {
  border: 1px solid rgba(217, 119, 6, 0.3) !important;
  color: #d97706 !important;
  background: rgba(217, 119, 6, 0.08) !important;
  box-shadow: none !important;
}

.btn-delete-lab:hover {
  background: rgba(217, 119, 6, 0.18) !important;
}

/* ── Processing card ───────────────────────── */
.processing-card {
  border-radius: 16px !important;
  overflow: hidden;
  padding: 0;
}

.processing-icon-wrap {
  display: flex;
  justify-content: center;
  padding: 28px 24px 0;
}

.processing-icon-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #eff6ff;
  border: 5px solid #bfdbfe;
  display: flex;
  align-items: center;
  justify-content: center;
}

.processing-icon {
  color: #254f87 !important;
  font-size: 26px !important;
  animation: spin-slow 1.4s linear infinite;
}

@keyframes spin-slow {
  to {
    transform: rotate(360deg);
  }
}

.processing-body {
  padding: 16px 28px 8px;
  text-align: center;
}

.processing-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 6px;
  letter-spacing: -0.01em;
}

.processing-subtitle {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
}

.processing-bar-wrap {
  padding: 16px 28px 24px;
}

.processing-bar {
  border-radius: 4px;
}

@media (max-width: 959px) {
  .list-title {
    font-size: 26px;
  }

  .prod-card__toolbar {
    padding: 10px;
  }

  .toolbar-actions,
  .toolbar-actions--end {
    justify-content: flex-start;
    margin-top: 8px;
  }

  .list-footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>

<style>
/* === Dialog del producto (no scoped: v-dialog se portala fuera) === */
/* Solo el v-dialog hace scroll. El form interno no tiene overflow propio. */
.prod-edit-dialog {
  margin: 24px;
}

/* Chips de filas con error en el mensaje de importación */
.err-chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: #fee2e2;
  color: #e5252a;
  white-space: nowrap;
}

.prod-edit-dialog__card {
  border-radius: 12px !important;
  overflow: visible !important;
}

/* Anular el scroll interno del form-page y su padding extra dentro del modal */
.prod-edit-dialog__card .prod-form-page {
  min-height: 0 !important;
  overflow: visible !important;
  padding: 20px 22px 24px !important;
  background: transparent !important;
}

.prod-edit-dialog__card .form-hero {
  margin-bottom: 16px;
}

.prod-edit-dialog__card .form-title {
  font-size: 22px;
}

.prod-edit-dialog__card .form-card {
  border: none !important;
  padding: 0 !important;
}

@media (max-width: 720px) {
  .prod-edit-dialog {
    margin: 0;
    border-radius: 0 !important;
  }
  .prod-edit-dialog__card {
    border-radius: 0 !important;
  }
}
</style>
